import type { operations } from "../api/schema";
import { facetSearchToValue, facetValueToSearch } from "../lib/facets";
import {
  MARKER_OUTPUT_FILTERS,
  markerEvaluatorFilterList,
  markerOriginFilterList,
  markerOutputFilterList,
  type MarkersSearch,
} from "../lib/routerSearch";
import type { FilterFacet, FilterOption, FilterValue } from "../ui";

export type MarkerListQuery = NonNullable<
  operations["markersList"]["parameters"]["query"]
>;

const FACET_IDS = ["output", "evaluator", "origin"] as const;

function enumLabel(token: string): string {
  return token
    .split("_")
    .map((word) => word.charAt(0).toLocaleUpperCase() + word.slice(1))
    .join(" ");
}

function enumOptions(tokens: readonly string[]): FilterOption[] {
  return tokens.map((value) => ({ label: enumLabel(value), value }));
}

export const MARKER_FACETS: FilterFacet[] = [
  {
    id: "output",
    kind: "multi",
    label: "Output",
    options: enumOptions(MARKER_OUTPUT_FILTERS),
  },
  {
    id: "evaluator",
    kind: "multi",
    label: "Evaluator",
    options: [
      { label: "Built-in", value: "builtin" },
      { label: "Human-only", value: "human_only" },
      { label: "Judge", value: "judge" },
      { label: "Rule", value: "rule" },
    ],
  },
  {
    id: "origin",
    kind: "multi",
    label: "Origin",
    options: [
      { label: "Built-in", value: "builtin" },
      { label: "Custom", value: "custom" },
    ],
  },
];

export function markerFacetValue(search: MarkersSearch): FilterValue {
  return facetSearchToValue(search, FACET_IDS, []);
}

export function markerFacetSearch(
  value: FilterValue,
): Pick<MarkersSearch, "output" | "evaluator" | "origin"> {
  return facetValueToSearch(value, FACET_IDS, []);
}

export function markerListFilters(search: MarkersSearch): MarkerListQuery {
  const outputs = markerOutputFilterList(search.output);
  const evaluators = markerEvaluatorFilterList(search.evaluator);
  const origins = markerOriginFilterList(search.origin);
  return {
    outputs: outputs.length > 0 ? outputs : undefined,
    evaluators: evaluators.length > 0 ? evaluators : undefined,
    origins: origins.length > 0 ? origins : undefined,
  };
}

export function markerFiltersToQueryKey(
  filters: MarkerListQuery,
): Readonly<Record<string, string | undefined>> {
  return {
    evaluators: filters.evaluators?.join(","),
    origins: filters.origins?.join(","),
    outputs: filters.outputs?.join(","),
  };
}
